import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { MultiSelectComponent, MultiSelectConfig } from '../../../@core/components/ui/multi-select';
import { FileDropComponent } from '../file-drop/file-drop.component';
import { PROJECT_KNOWLEDGE_BASE } from '../../../@core/models/enums';

export interface ProjectKnowledgeDialogResult {
    knowledgeBases: PROJECT_KNOWLEDGE_BASE[];
    files: File[];
}

@Component({
    selector: 'app-project-knowledge-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DialogComponent,
        RadioButtonModule,
        ButtonComponent,
        MultiSelectComponent,
        FileDropComponent
    ],
    templateUrl: './project-knowledge-dialog.component.html',
    styleUrls: ['./project-knowledge-dialog.component.scss']
})
export class ProjectKnowledgeDialogComponent implements OnInit, OnChanges {
    @Input() visible: boolean = false;
    @Input() existingFiles: File[] = [];
    @Input() existingKnowledgeBases: PROJECT_KNOWLEDGE_BASE[] = [];
    @Input() existingUploadMode: 'withKB' | 'withoutKB' = 'withKB';
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() upload = new EventEmitter<ProjectKnowledgeDialogResult>();
    @Output() close = new EventEmitter<void>();

    PROJECT_KNOWLEDGE_BASE = PROJECT_KNOWLEDGE_BASE;

    uploadMode: 'withKB' | 'withoutKB' = 'withKB';
    selectedKnowledgeBases: PROJECT_KNOWLEDGE_BASE[] = [];
    selectedFiles: File[] = [];
    private wasUploaded: boolean = false;


    // Knowledge base options for multi-select
    knowledgeBaseOptions = [
        { label: PROJECT_KNOWLEDGE_BASE.SPECIFICATIONS, value: PROJECT_KNOWLEDGE_BASE.SPECIFICATIONS },
        { label: PROJECT_KNOWLEDGE_BASE.ADDENDUMS, value: PROJECT_KNOWLEDGE_BASE.ADDENDUMS },
        { label: PROJECT_KNOWLEDGE_BASE.CONTRACTS, value: PROJECT_KNOWLEDGE_BASE.CONTRACTS },
        { label: PROJECT_KNOWLEDGE_BASE.BILL_OF_MATERIALS, value: PROJECT_KNOWLEDGE_BASE.BILL_OF_MATERIALS },
        { label: PROJECT_KNOWLEDGE_BASE.PROPOSALS, value: PROJECT_KNOWLEDGE_BASE.PROPOSALS }
    ];

    multiSelectConfig: MultiSelectConfig = {
        placeholder: 'Select knowledge base',
        filter: true,
        style: { width: '100%' },
        styleClass: 'border',
        appendTo: 'body' as const
    };

    ngOnInit(): void {
        this.initializeFromExisting();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible && !changes['visible'].previousValue) {
            // Dialog just opened - initialize with existing data
            this.initializeFromExisting();
        }
        // Update when existing data changes
        if (changes['existingFiles'] || changes['existingKnowledgeBases'] || changes['existingUploadMode']) {
            if (this.visible) {
                this.initializeFromExisting();
            }
        }
    }

    initializeFromExisting(): void {
        // Initialize with existing data if available, otherwise use defaults
        this.selectedFiles = this.existingFiles.length > 0 ? [...this.existingFiles] : [];
        this.selectedKnowledgeBases = this.existingKnowledgeBases.length > 0 ? [...this.existingKnowledgeBases] : [];
        this.uploadMode = this.existingUploadMode || 'withKB';
    }

    reset(): void {
        this.uploadMode = 'withKB';
        this.selectedKnowledgeBases = [];
        this.selectedFiles = [];
    }

    onUploadModeChange(mode: 'withKB' | 'withoutKB'): void {
        this.uploadMode = mode;
        if (mode === 'withoutKB') {
            this.selectedKnowledgeBases = [];
        }
    }

    onFilesSelected(files: File | File[]): void {
        // file-drop component emits the entire array of current files
        if (Array.isArray(files)) {
            this.selectedFiles = [...files];
        }
        else {
            this.selectedFiles = [files];
        }
    }

    onFileRemoved(file: File): void {
        // Remove the file by comparing name, size, and lastModified
        this.selectedFiles = this.selectedFiles.filter(f =>
            !(f.name === file.name && f.size === file.size && f.lastModified === file.lastModified)
        );
    }

    isUploadEnabled(): boolean {
        // At least one file must be selected
        if (this.selectedFiles.length === 0) {
            return false;
        }

        // If "with knowledge base" mode is selected, at least one knowledge base must be selected
        if (this.uploadMode === 'withKB' && this.selectedKnowledgeBases.length === 0) {
            return false;
        }

        return true;
    }

    onUpload(): void {
        if (!this.isUploadEnabled()) {
            return;
        }

        const result: ProjectKnowledgeDialogResult = {
            knowledgeBases: this.uploadMode === 'withKB' ? this.selectedKnowledgeBases : [],
            files: this.selectedFiles
        };

        this.wasUploaded = true;
        this.upload.emit(result);
        // Don't reset here - let parent handle the state
    }

    onClose(): void {
        // Closing with X always restores the parent to exactly what it had before
        // the dialog was opened — any files added but not explicitly uploaded are discarded
        if (!this.wasUploaded) {
            const result: ProjectKnowledgeDialogResult = {
                knowledgeBases: [...this.existingKnowledgeBases],
                files: [...this.existingFiles]
            };
            this.upload.emit(result);
        }

        this.wasUploaded = false; // Reset flag for next open
        this.visibleChange.emit(false);
        this.close.emit();
        // Don't reset on close - preserve state for next open
    }

    onDialogHide(): void {
        this.onClose();
    }
}

